import { z } from "zod";

/**
 * KPI-Modul (Stufe 2) — geteilte Shapes für Bereichsziele und Planungsbasis.
 */

const yearMonthSchema = z.string().regex(/^\d{4}-\d{2}-01$/);

/** Monatszeile der Zielpflege. */
export const segmentTargetMonthSchema = z.object({
  yearMonth: yearMonthSchema,
  revenueTargetEur: z.number().nonnegative().nullable(),
  billedHoursTarget: z.number().nonnegative().nullable(),
  /**
   * Der beim Einfrieren der Zielrunde festgehaltene Umsatz dieses Monats.
   * Nur in der ANTWORT gesetzt (server-berechnet) — beim Schreiben (PUT)
   * optional/ungenutzt, der Server liest ihn dort nicht.
   */
  planRevenueTargetEur: z.number().nullable().optional(),
  /** Begründung der letzten Änderung dieses Monats NACH dem Einfrieren. */
  planChangeReason: z.string().nullable().optional(),
});

/** Body für PUT /kpi/segments/:segmentId/targets/:year. */
export const segmentTargetsPayloadSchema = z.object({
  revenueTargetEur: z.number().nonnegative().nullable(),
  growthTargetPct: z.number().min(-100).max(999).nullable(),
  contributionMarginTargetPct: z.number().min(0).max(100).nullable(),
  sickDaysTarget: z.number().min(0).max(999).nullable(),
  months: z.array(segmentTargetMonthSchema).length(12),
  /**
   * Pflicht, sobald die Zielrunde eingefroren ist — der Server erzwingt es,
   * nicht der Dialog. Mindestens 10 Zeichen: „ok" ist keine Begründung.
   */
  begruendung: z.string().min(10).max(500).optional(),
  /**
   * Der beim Einfrieren der Zielrunde festgehaltene Jahresumsatz — „was war
   * geplant" neben „was gilt heute" (Zielrunde-Spec, Abschnitt Oberfläche).
   * Nur in der ANTWORT gesetzt; beim Schreiben (PUT) optional/ungenutzt.
   */
  planRevenueTargetEur: z.number().nullable().optional(),
  /** Begründung der letzten Änderung des Jahresziels NACH dem Einfrieren. */
  planChangeReason: z.string().nullable().optional(),
});
export type SegmentTargetsPayload = z.infer<typeof segmentTargetsPayloadSchema>;

/** Response von GET /kpi/segments/:segmentId/planning-basis. */
export const planningBasisResponseSchema = z.object({
  year: z.number().int(),
  referenceFederalState: z.string(),
  /** Vorjahresumsatz des Segments — Basis der €⇄%-Kopplung. */
  previousYearRevenueEur: z.number(),
  /** Bottom-up: Σ der Beauftragungs-Jahresziele des Segments. */
  engagementTargetsSumEur: z.number(),
  /** Wie viele der Beauftragungen des Segments ein Ziel gepflegt haben. */
  engagementTargetsCount: z.number().int(),
  engagementCount: z.number().int(),
  months: z
    .array(
      z.object({
        yearMonth: yearMonthSchema,
        workingDays: z.number().int().nonnegative(),
        /** Historischer Umsatzanteil des Monats (Vorjahr), Summe = 1. */
        seasonShare: z.number().min(0).max(1),
        /** Berechnete Team-Kapazität (inkl. Stunden-Zeiträume, ohne Abwesenheiten). */
        computedBilledHoursTarget: z.number(),
      }),
    )
    .length(12),
});
export type PlanningBasisResponse = z.infer<typeof planningBasisResponseSchema>;

/**
 * Personio-Abwesenheitstypen, die als Krankheit zählen (KPI „Krankheitstage").
 * Konstante statt Settings-UI, bis die echten Typnamen aus dem
 * Personio-Export bekannt sind (Import ist vertagt).
 */
export const SICK_ABSENCE_TYPES = ["Krankheit", "Krank", "Kind krank"];

/**
 * Typ, den der Personio-API-Abruf schreibt.
 *
 * Entscheidung 2026-08-03: **Für die Abwesenheiten ist der Typ irrelevant — ein
 * abwesender Tag ist ein abwesender Tag.** Deshalb wird gar kein Grund erhoben,
 * sondern konstant dieser Wert gesetzt. Nebeneffekt: Der Grund wäre bei
 * Krankheit ein Gesundheitsdatum nach Art. 9 DSGVO, das damit erst gar nicht
 * ins System kommt.
 *
 * Der Wert steht bewusst NICHT in `SICK_ABSENCE_TYPES`: API-Tage mindern die
 * Kapazität, tauchen aber nicht im Krankheitstage-KPI auf. Der bleibt
 * unverändert dem CSV-Import vorbehalten.
 */
export const PERSONIO_NEUTRAL_ABSENCE_TYPE = "abwesend";

/** Monatszeile der Bereichs-KPIs (GET /kpi/segments/:id/monthly). */
export const kpiSegmentMonthSchema = z.object({
  yearMonth: yearMonthSchema,
  revenue: z.object({
    target: z.number().nullable(),
    actual: z.number(),
    prevYear: z.number(),
    fixedPrice: z.number(),
    invoiced: z.number(),
    /** Monats-Wachstum ggü. Vorjahresmonat; null wenn Vorjahr 0. */
    growthPct: z.number().nullable(),
    /** Kumuliertes YTD-Wachstum ggü. Vorjahres-YTD; null wenn Basis 0. */
    growthCumPct: z.number().nullable(),
  }),
  margin: z.object({
    pct: z.number().nullable(),
    cumPct: z.number().nullable(),
  }),
  hours: z.object({
    /** Soll: manueller Override oder Kapazität (AT − Abwesenheit) × h/Tag. */
    target: z.number(),
    actualIntern: z.number(),
    actualExtern: z.number(),
    /**
     * Stunden, die Team-Mitglieder auf Projekten ANDERER Bereiche gebucht
     * haben. Die Soll-Linie ist die Kapazitaet dieses Teams — ohne diesen Wert
     * sah ein Mitglied, das einen Monat woanders arbeitet, wie eine Luecke aus.
     * Die Oberflaeche addiert ihn wahlweise zum Intern-Balken.
     */
    actualInternElsewhere: z.number(),
    /**
     * Stunden auf Projekten AUSSERHALB des Servicegeschaefts
     * (Salesforce-Bereich != 'Service', in aller Regel Projektumsetzung).
     *
     * Zweite Sorte Zusatzstunden neben `actualInternElsewhere`: dort ein
     * anderes KUNDENSEGMENT, hier ein anderer SALESFORCE-BEREICH. Beide
     * schaltet die Oberflaeche mit demselben Haken zu, weil der Betrachter
     * eine Entscheidung trifft und nicht vier Zustaende.
     */
    actualInternNonService: z.number(),
    /** Ist_intern / Soll; null wenn Soll 0. */
    utilizationPct: z.number().nullable(),
  }),
  absenceDays: z.number(),
  sickDays: z.number(),
});

export const kpiSegmentMonthlyResponseSchema = z.object({
  year: z.number().int(),
  segmentId: z.string().uuid(),
  marginTargetPct: z.number().nullable(),
  months: z.array(kpiSegmentMonthSchema).length(12),
});
export type KpiSegmentMonthlyResponse = z.infer<
  typeof kpiSegmentMonthlyResponseSchema
>;

/** ---------------------------------------------------- Ausreißer-Analyse -- */

export const kpiOutlierModeSchema = z.enum(["customer", "employee"]);
export type KpiOutlierMode = z.infer<typeof kpiOutlierModeSchema>;

/** Query von GET /kpi/outliers. */
export const kpiOutliersQuerySchema = z.object({
  segmentId: z.string().uuid(),
  mode: kpiOutlierModeSchema.default("customer"),
  // Mindestens 7: 6er-Median-Fenster + 1 bewertbarer Monat.
  months: z.coerce.number().int().min(7).max(48).default(24),
  threshold: z.coerce.number().min(0.05).max(5).default(0.5),
  // Absolute Mindestabweichung — filtert Kleinstbeträge-Rauschen.
  minDeviation: z.coerce.number().min(0).max(1_000_000).default(5000),
  // Nur die Kunden, fuer die der Aufrufer Service Lead ist.
  nurEigene: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
});

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Body von POST /kpi/segments/:segmentId/team. */
export const addSegmentTeamMembersSchema = z.object({
  employeeIds: z.array(z.string().uuid()).min(1).max(200),
  /** Eintritt ab (Default: heute) — Basis der Kapazitäts-Historie. */
  validFrom: isoDateSchema.optional(),
});

/** Body von POST /kpi/segments/:segmentId/team/:employeeId/end. */
export const endSegmentTeamMembershipSchema = z.object({
  /** Datum = Austritt setzen/korrigieren; null = Austritt aufheben. */
  validTo: isoDateSchema.nullable().optional(),
});

/** Body von POST /kpi/segments/:segmentId/team/:employeeId/since. */
export const redateSegmentTeamMembershipSchema = z.object({
  validFrom: isoDateSchema,
});

/** Body von PUT /kpi/segments/:segmentId/zielrunde/:year/freigabe. */
export const zielrundeFreigabeSchema = z.object({ freigegeben: z.boolean() });

/** Zelle der Ausreißer-Matrix — Kontextwerte für Tooltip/Erklärung. */
export const kpiOutlierCellSchema = z.object({
  value: z.number(),
  flag: z.boolean(),
  /** Median der Vormonats-Fensters; null solange das Fenster unvollständig ist. */
  median: z.number().nullable(),
  /** Abweichung vom Median in % (null bei Median 0 / unvollständigem Fenster). */
  deltaPct: z.number().nullable(),
});

/** Response von GET /kpi/outliers. */
export const kpiOutliersResponseSchema = z.object({
  mode: kpiOutlierModeSchema,
  segmentId: z.string().uuid(),
  threshold: z.number(),
  /** Absolute Mindestabweichung in € — unterhalb wird nie geflaggt. */
  minDeviationEur: z.number(),
  /** Fensterbreite des rollierenden Medians (Anzahl Vormonate). */
  medianWindow: z.number().int(),
  months: z.array(yearMonthSchema),
  rows: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      /** Nur employee-Modus: Karrierestufe aus dem Profil (Name-Match). */
      careerLevel: z.string().nullable(),
      /** employee-Modus: kein eindeutiger Mitarbeiter-Match → extern. */
      external: z.boolean(),
      /** employee-Modus: eindeutig gematchter Mitarbeiter (für „+ Team"). */
      employeeId: z.string().uuid().nullable(),
      /** Home-Bereich des gematchten Mitarbeiters (employee_profiles). */
      homeSegmentId: z.string().uuid().nullable(),
      total: z.number(),
      cells: z.array(kpiOutlierCellSchema),
    }),
  ),
});
export type KpiOutliersResponse = z.infer<typeof kpiOutliersResponseSchema>;
export type KpiOutlierCell = z.infer<typeof kpiOutlierCellSchema>;

/** ------------------------------------------------------------ Zielrunde -- */

/** Zustand der Zielrunde samt Pflegestand je Service Lead. */
export const zielrundeResponseSchema = z.object({
  status: z.enum(["offen", "eingefroren"]),
  frozenAt: z.string().nullable(),
  frozenByName: z.string().nullable(),
  leads: z.array(
    z.object({
      employeeId: z.string().uuid(),
      fullName: z.string(),
      beauftragungen: z.number().int(),
      mitZiel: z.number().int(),
      freigegebenAt: z.string().nullable(),
    }),
  ),
  /**
   * Beauftragungen des Bereichs OHNE Service Lead (`lead_employee_id IS
   * NULL`). Die Leads-Liste kommt aus einem JOIN über den Lead — ohne diese
   * Zeile verschwinden sie aus der Ansicht, obwohl `friereZielrundeEin` sie
   * mit einfriert.
   */
  ohneLead: z.object({
    beauftragungen: z.number().int(),
    mitZiel: z.number().int(),
  }),
});
export type ZielrundeResponse = z.infer<typeof zielrundeResponseSchema>;

export const OUTLIER_MEDIAN_WINDOW = 6;
/** Iglewicz/Hoaglin: modified z-score > 3.5 gilt als Ausreißer. */
const OUTLIER_ROBUST_Z = 3.5;

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]!
    : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Ausreißer-Zellen über eine Monatsreihe — Drei-Kriterien-Flag, alle müssen
 * gleichzeitig gelten (Befund 2026-07-26: reiner Median-Vergleich flutete
 * die Matrix — sporadische Rechnungssteller und Kleinstbeträge):
 *
 *  1. RELATIV:  |Wert/Median − 1| ≥ threshold (bisheriges Kriterium)
 *  2. ABSOLUT:  |Wert − Median| ≥ minDeviation — Kleinstbeträge flaggen nie
 *  3. ROBUST:   modified z-score (0.6745·|Wert−Median|/MAD) ≥ 3.5 — der Wert
 *     liegt außerhalb der für DIESE Reihe üblichen Schwankungsbreite.
 *     MAD = 0 (exakt konstante Reihe) → Kriterium entfällt, damit der
 *     Abriss eines stabilen Kunden weiter flaggt.
 *
 * Kein Flag ohne volles Fenster (Reihen-Anfang) oder bei Median 0 —
 * Anlauf- und Leerphasen sollen nicht als Ausreißer leuchten.
 */
export function computeOutlierCells(
  series: number[],
  threshold: number,
  minDeviation: number,
  window: number = OUTLIER_MEDIAN_WINDOW,
): KpiOutlierCell[] {
  return series.map((value, i) => {
    if (i < window) {
      return { value, flag: false, median: null, deltaPct: null };
    }
    const win = series.slice(i - window, i);
    const med = median(win);
    if (med === 0) {
      return { value, flag: false, median: 0, deltaPct: null };
    }
    const delta = Math.abs(value - med);
    const deltaPct = (value / med - 1) * 100;

    const relativeHit = Math.abs(value / med - 1) >= threshold;
    const absoluteHit = delta >= minDeviation;
    const mad = median(win.map((v) => Math.abs(v - med)));
    const robustHit = mad === 0 || (0.6745 * delta) / mad >= OUTLIER_ROBUST_Z;

    return {
      value,
      flag: relativeHit && absoluteHit && robustHit,
      median: med,
      deltaPct,
    };
  });
}

/**
 * Verteilt ein Jahresziel auf 12 Monate nach gewichteten Profilen
 * (gleichmäßig / Arbeitstage / Saison). Rundet auf ganze Euro; der
 * Rundungsrest landet im letzten Monat, damit die Summe exakt dem
 * Jahresziel entspricht.
 */
export function distributeTarget(
  annualTarget: number,
  weights: { uniform: number; workingDays: number; season: number },
  workingDaysByMonth: number[],
  seasonShareByMonth: number[],
): number[] {
  const wSum = weights.uniform + weights.workingDays + weights.season;
  if (wSum <= 0 || workingDaysByMonth.length !== 12) {
    return Array.from({ length: 12 }, () => Math.round(annualTarget / 12));
  }

  const wdTotal = workingDaysByMonth.reduce((a, b) => a + b, 0);
  const seasonTotal = seasonShareByMonth.reduce((a, b) => a + b, 0);

  const shares = Array.from({ length: 12 }, (_, i) => {
    const uniform = 1 / 12;
    const wd = wdTotal > 0 ? workingDaysByMonth[i]! / wdTotal : 1 / 12;
    const season =
      seasonTotal > 0 ? seasonShareByMonth[i]! / seasonTotal : 1 / 12;
    return (
      (weights.uniform * uniform +
        weights.workingDays * wd +
        weights.season * season) /
      wSum
    );
  });

  const values = shares.map((s) => Math.round(annualTarget * s));
  const diff = Math.round(annualTarget) - values.reduce((a, b) => a + b, 0);
  values[11] = (values[11] ?? 0) + diff;
  return values;
}
